/**
 * HealthProviderDetail — /health/providers/:slug
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  UserRound, MapPin, Phone, Mail, Clock, GraduationCap, Award,
  Building2, CalendarPlus, Loader2,
} from "lucide-react";
import { http } from "@/lib/api";
import HealthCategoryNav from "@/components/HealthCategoryNav";

export default function HealthProviderDetail() {
  const { slug } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http.get(`/health/providers/${slug}`)
      .then(r => setDoc(r.data))
      .catch(() => setDoc(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <><HealthCategoryNav /><div className="max-w-4xl mx-auto p-8 opacity-60 inline-flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin"/>Loading…</div></>;
  if (!doc) return <><HealthCategoryNav /><div className="max-w-4xl mx-auto p-8">Not found.</div></>;

  return (
    <>
      <HealthCategoryNav />
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="provider-detail">
        <div className="flex items-start gap-4 mb-6">
          {doc.photo_url ? (
            <img src={doc.photo_url} alt="" className="w-20 h-20 rounded-full object-cover"/>
          ) : (
            <div className="w-20 h-20 rounded-full bg-black/5 grid place-items-center"><UserRound className="w-9 h-9"/></div>
          )}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-1">
              {doc.profession_group}{doc.specialty ? ` · ${doc.specialty}` : ""}
            </div>
            <h1 className="font-display text-4xl mb-1">{doc.name}</h1>
            {(doc.city || doc.country) && (
              <div className="text-sm opacity-70 flex items-center gap-1"><MapPin className="w-3 h-3"/>{[doc.city, doc.state, doc.country].filter(Boolean).join(", ")}</div>
            )}
          </div>
        </div>

        {doc.biography && (
          <section className="mb-6">
            <h2 className="font-display text-xl mb-2">Biography</h2>
            <p className="whitespace-pre-line opacity-90">{doc.biography}</p>
          </section>
        )}

        <div className="grid gap-4 md:grid-cols-2 mb-6">
          {doc.qualifications?.length > 0 && (
            <div className="wofy-card !p-4">
              <div className="text-xs opacity-70 mb-2 inline-flex items-center gap-1"><GraduationCap className="w-3 h-3"/>Qualifications</div>
              <ul className="text-sm list-disc ml-4 space-y-0.5">{doc.qualifications.map((q,i) => <li key={i}>{q}</li>)}</ul>
            </div>
          )}
          {doc.certifications?.length > 0 && (
            <div className="wofy-card !p-4">
              <div className="text-xs opacity-70 mb-2 inline-flex items-center gap-1"><Award className="w-3 h-3"/>Certifications</div>
              <ul className="text-sm list-disc ml-4 space-y-0.5">{doc.certifications.map((q,i) => <li key={i}>{q}</li>)}</ul>
            </div>
          )}
          {doc.hospital_affiliations?.length > 0 && (
            <div className="wofy-card !p-4">
              <div className="text-xs opacity-70 mb-2 inline-flex items-center gap-1"><Building2 className="w-3 h-3"/>Hospital affiliations</div>
              <ul className="text-sm list-disc ml-4 space-y-0.5">{doc.hospital_affiliations.map((q,i) => <li key={i}>{q}</li>)}</ul>
            </div>
          )}
          {doc.languages?.length > 0 && (
            <div className="wofy-card !p-4">
              <div className="text-xs opacity-70 mb-2">Languages</div>
              <div className="flex flex-wrap gap-1.5">
                {doc.languages.map((l,i) => <span key={i} className="text-xs px-2 py-1 rounded-full border" style={{ borderColor: "var(--wofy-border)" }}>{l}</span>)}
              </div>
            </div>
          )}
          {doc.office_hours && (
            <div className="wofy-card !p-4">
              <div className="text-xs opacity-70 mb-1 inline-flex items-center gap-1"><Clock className="w-3 h-3"/>Office hours</div>
              <div className="text-sm whitespace-pre-line">{doc.office_hours}</div>
            </div>
          )}
          {doc.contact_phone && (
            <div className="wofy-card !p-4">
              <div className="text-xs opacity-70 mb-1 inline-flex items-center gap-1"><Phone className="w-3 h-3"/>Phone</div>
              <a href={`tel:${doc.contact_phone}`} className="text-sm wofy-link">{doc.contact_phone}</a>
            </div>
          )}
          {doc.contact_email && (
            <div className="wofy-card !p-4">
              <div className="text-xs opacity-70 mb-1 inline-flex items-center gap-1"><Mail className="w-3 h-3"/>Email</div>
              <a href={`mailto:${doc.contact_email}`} className="text-sm wofy-link">{doc.contact_email}</a>
            </div>
          )}
        </div>

        {doc.accepts_appointments && (
          <Link to={`/health/appointments/new?provider_slug=${encodeURIComponent(doc.slug)}`}
            className="wofy-btn-primary inline-flex items-center gap-1.5" data-testid="request-appointment">
            <CalendarPlus className="w-4 h-4"/>Request an appointment
          </Link>
        )}
      </div>
    </>
  );
}
